import { getBoideToken } from "@/lib/boide-api";

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

export function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px), (pointer: coarse)").matches;
}

export function supportsWebPush(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function base64UrlToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export async function registerPwaServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

export async function subscribeToWebPush(): Promise<PushSubscription> {
  const publicKey = import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY as string | undefined;
  if (!publicKey) throw new Error("Tính năng thông báo chưa được cấu hình trên hệ thống");
  if (!getBoideToken()) throw new Error("Vui lòng đăng nhập trước khi bật thông báo");
  if (Notification.permission === "denied") {
    throw new Error("Thông báo đang bị chặn. Hãy cho phép lại trong cài đặt trình duyệt");
  }

  // Chrome Android chỉ hiện permission prompt khi lời gọi này còn nằm trong user gesture.
  // Khởi tạo nó ngay trước mọi await khác; Service Worker được chuẩn bị song song.
  const permissionPromise = Notification.requestPermission();
  const registrationPromise = registerPwaServiceWorker();
  const [permission, registration] = await Promise.all([permissionPromise, registrationPromise]);
  if (!registration) throw new Error("Trình duyệt không hỗ trợ Service Worker");
  if (permission !== "granted") throw new Error("Bạn chưa cho phép Bói Đê gửi thông báo");

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  }
  return subscription;
}
